import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../lib/http';
import {
  PortalButton,
  PortalCard,
  PortalEmpty,
  PortalInput,
  PortalSelect,
  PortalStatus,
} from '../../components/portal/PortalElements';

export default function AdminAvailability() {
  const [availability, setAvailability] = useState(null);
  const [closure, setClosure] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    entireDay: true,
    reason: '',
    displayReasonToCustomers: false,
  });

  async function loadAvailability() {
    const response = await adminApi.get('/admin/availability');
    setAvailability(response.data);
  }

  useEffect(() => {
    loadAvailability().catch((error) => toast.error(error.message || 'Unable to load availability.'));
  }, []);

  const activeClosures = useMemo(
    () => (availability?.closures || []).filter((item) => item.active !== false),
    [availability],
  );

  async function saveClosure() {
    try {
      await adminApi.post('/admin/availability/closures', {
        ...closure,
        endDate: closure.endDate || closure.startDate,
      });
      toast.success('Closure created.');
      setClosure({
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        entireDay: true,
        reason: '',
        displayReasonToCustomers: false,
      });
      await loadAvailability();
    } catch (error) {
      toast.error(error.message || 'Unable to save closure.');
    }
  }

  async function undoClosure(id) {
    try {
      await adminApi.patch(`/admin/availability/closures/${id}/undo`);
      toast.success('Closure removed.');
      await loadAvailability();
    } catch (error) {
      toast.error(error.message || 'Unable to undo closure.');
    }
  }

  return (
    <div className="portal-stack">
      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Closures</p>
            <h2>Mark unavailable</h2>
          </div>
        </div>
        <div className="portal-grid portal-grid-2">
          <PortalInput
            label="Start date"
            type="date"
            value={closure.startDate}
            onChange={(event) => setClosure((current) => ({ ...current, startDate: event.target.value }))}
          />
          <PortalInput
            label="End date"
            type="date"
            value={closure.endDate}
            onChange={(event) => setClosure((current) => ({ ...current, endDate: event.target.value }))}
          />
          <PortalSelect
            label="Duration"
            value={closure.entireDay ? 'entire' : 'partial'}
            onChange={(event) => setClosure((current) => ({ ...current, entireDay: event.target.value === 'entire' }))}
          >
            <option value="entire">Entire day</option>
            <option value="partial">Partial day</option>
          </PortalSelect>
          <PortalInput
            label="Reason"
            value={closure.reason}
            onChange={(event) => setClosure((current) => ({ ...current, reason: event.target.value }))}
            placeholder="Holiday closure"
          />
          {!closure.entireDay ? (
            <>
              <PortalInput
                label="Start time"
                type="time"
                value={closure.startTime}
                onChange={(event) => setClosure((current) => ({ ...current, startTime: event.target.value }))}
              />
              <PortalInput
                label="End time"
                type="time"
                value={closure.endTime}
                onChange={(event) => setClosure((current) => ({ ...current, endTime: event.target.value }))}
              />
            </>
          ) : null}
        </div>
        <label className="portal-checkbox">
          <input
            type="checkbox"
            checked={closure.displayReasonToCustomers}
            onChange={(event) =>
              setClosure((current) => ({
                ...current,
                displayReasonToCustomers: event.target.checked,
              }))
            }
          />
          <span>Show the reason to customers</span>
        </label>
        <div className="portal-action-row">
          <PortalButton onClick={saveClosure}>Save closure</PortalButton>
        </div>
      </PortalCard>

      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Active records</p>
            <h2>Current closures</h2>
          </div>
        </div>
        {activeClosures.length ? (
          <div className="portal-list">
            {activeClosures.map((item) => (
              <div key={item._id} className="portal-list-item">
                <div>
                  <strong>{item.startDate}{item.endDate && item.endDate !== item.startDate ? ` to ${item.endDate}` : ''}</strong>
                  <p>{item.entireDay ? 'Entire day' : `${item.startTime} to ${item.endTime}`}</p>
                  <PortalStatus>{item.reason || 'Unavailable'}</PortalStatus>
                </div>
                <PortalButton variant="secondary" onClick={() => undoClosure(item._id)}>
                  Undo
                </PortalButton>
              </div>
            ))}
          </div>
        ) : (
          <PortalEmpty title="No closures active" body="The calendar is open right now." />
        )}
      </PortalCard>
    </div>
  );
}
